import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { OVERLAY_EXIT_MS } from 'pi-kiosk-shared/ui';
import { CUSTOMER_SHELL_WIDTH_CLASS } from '../../app/customerTopBarStyles.js';
import { Toast } from './Toast.js';
import {
  dismissToast,
  getToastSnapshot,
  resolveToastAriaLive,
  showToast,
  subscribeToasts,
  type ToastRecord,
} from './toastStore.js';

/** Spec / EXC-CUST-P9-TOAST-POS: prefer usually 1, hard cap 2 visible. */
export const MAX_VISIBLE_TOASTS = 2;

function visibleSlice(queue: readonly ToastRecord[]): readonly ToastRecord[] {
  return queue.slice(0, MAX_VISIBLE_TOASTS);
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Mount once under CustomerAppShell (Wave B). Renders up to two toasts stacked
 * at the bottom above BottomNav + StickyCart + safe-area. Does not steal focus.
 * Dismiss marks exit locally and holds DOM for OVERLAY_EXIT_MS (0 when reduce).
 */
export function ToastHost(): JSX.Element {
  const queue = useSyncExternalStore(subscribeToasts, getToastSnapshot, getToastSnapshot);
  const visible = visibleSlice(queue);
  const timersRef = useRef(new Map<string, number>());
  const exitTimersRef = useRef(new Map<string, number>());
  const [exitingIds, setExitingIds] = useState<ReadonlySet<string>>(() => new Set());

  const clearAutoTimer = useCallback((id: string): void => {
    const handle = timersRef.current.get(id);
    if (handle === undefined) {
      return;
    }
    window.clearTimeout(handle);
    timersRef.current.delete(id);
  }, []);

  const beginExit = useCallback(
    (id: string): void => {
      clearAutoTimer(id);

      if (exitTimersRef.current.has(id)) {
        return;
      }

      // Match overlay presence: skip exit hold when reduce.
      if (prefersReducedMotion()) {
        dismissToast(id);
        return;
      }

      // Sync guard so a second dismiss before state commits cannot double-schedule.
      exitTimersRef.current.set(id, 0);

      setExitingIds((prev) => {
        if (prev.has(id)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(id);
        return next;
      });

      const handle = window.setTimeout(() => {
        exitTimersRef.current.delete(id);
        setExitingIds((prev) => {
          if (!prev.has(id)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        dismissToast(id);
      }, OVERLAY_EXIT_MS);
      exitTimersRef.current.set(id, handle);
    },
    [clearAutoTimer],
  );

  // Prune exit bookkeeping when store removes an id (render-time sync — no effect setState).
  const queueIds = new Set(queue.map((toast) => toast.id));
  let exitingNeedsPrune = false;
  for (const id of exitingIds) {
    if (!queueIds.has(id)) {
      exitingNeedsPrune = true;
      break;
    }
  }
  if (exitingNeedsPrune) {
    const next = new Set<string>();
    for (const id of exitingIds) {
      if (queueIds.has(id)) {
        next.add(id);
      }
    }
    setExitingIds(next);
  }

  useEffect(() => {
    const timers = timersRef.current;
    const visibleIds = new Set(visible.map((toast) => toast.id));

    for (const [id, handle] of timers) {
      if (!visibleIds.has(id)) {
        window.clearTimeout(handle);
        timers.delete(id);
      }
    }

    for (const toast of visible) {
      if (timers.has(toast.id) || exitingIds.has(toast.id) || exitTimersRef.current.has(toast.id)) {
        continue;
      }
      if (!Number.isFinite(toast.durationMs) || toast.durationMs <= 0) {
        continue;
      }
      const handle = window.setTimeout(() => {
        timers.delete(toast.id);
        beginExit(toast.id);
      }, toast.durationMs);
      timers.set(toast.id, handle);
    }
  }, [visible, exitingIds, beginExit]);

  useEffect(() => {
    const ids = new Set(queue.map((toast) => toast.id));
    for (const [id, handle] of exitTimersRef.current) {
      if (!ids.has(id)) {
        window.clearTimeout(handle);
        exitTimersRef.current.delete(id);
      }
    }
  }, [queue]);

  useEffect(() => {
    const timers = timersRef.current;
    const exitTimers = exitTimersRef.current;
    return (): void => {
      for (const handle of timers.values()) {
        window.clearTimeout(handle);
      }
      timers.clear();
      for (const handle of exitTimers.values()) {
        window.clearTimeout(handle);
      }
      exitTimers.clear();
    };
  }, []);

  // DEV/e2e bridge — same pattern as admin `__adminConfirmApiRequest` (Playwright hermetic stimulus).
  // Avoid `import.meta` — Jest cannot parse it in this module graph.
  useEffect(() => {
    const exposeBridge = process.env.NODE_ENV !== 'production';
    if (!exposeBridge) {
      return;
    }
    window.__RPAPP_E2E_SHOW_TOAST__ = showToast;
    return (): void => {
      delete window.__RPAPP_E2E_SHOW_TOAST__;
    };
  }, []);

  return (
    <div
      data-testid="customer-toast-host"
      className={`customer-safe-area-x pointer-events-none fixed inset-x-0 z-50 flex flex-col-reverse gap-2 px-3 ${CUSTOMER_SHELL_WIDTH_CLASS} lg:[html[data-sidebar-visible]_&]:pl-56`}
      style={{
        bottom:
          'max(var(--customer-toast-offset, 5.5rem), env(safe-area-inset-bottom, 0px))',
      }}
      aria-relevant="additions text"
    >
      {visible.map((toast) => {
        const ariaLive = resolveToastAriaLive(toast.variant);
        const open = !exitingIds.has(toast.id);
        return (
          <div key={toast.id} className="pointer-events-auto" data-toast-id={toast.id}>
            <Toast
              message={toast.message}
              variant={toast.variant}
              open={open}
              onDismiss={() => {
                beginExit(toast.id);
              }}
              testId={`customer-toast-${toast.variant}`}
              aria-live={ariaLive}
              role={ariaLive === 'assertive' ? 'alert' : 'status'}
            />
          </div>
        );
      })}
    </div>
  );
}
